import React, { useContext, useEffect, useState } from "react";
import HappyHourApi from "../api/backendApi.js";
import SpaceCard from "./SpaceCard.js";
import UserContext from "../context/UserContext.js";
import { Link } from "react-router-dom";
import "./SpacesList.css";

/** Show page with list of spaces
 *
 * Loads spaces from HappyHour API.
 * Re-loads filtered spaces on submit from search form.
 *
 * Routed to /spaces
 * Routes -> { SpaceCard, SearchForm}
 * Must be logged in to see spaces
 */

const SpacesListWithSearch = ({ remove }) => {
  const { currentUser } = useContext(UserContext);
  console.debug("SpacesListWithSearchBar");

  const [spaces, setSpaces] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    rating: "",
    neighborhood: "",
    city: "",
    category: "",
    sortBy: "",
  });
  const [categories, setCategories] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        let apiParams = { title: searchTerm, ...filters };
        if (filters.category) apiParams.category = filters.category;
        if (filters.sortBy) apiParams.sortBy = filters.sortBy;
        if (filters.city) apiParams.city = filters.city;
        if (filters.neighborhood) apiParams.neighborhood = filters.neighborhood;

        const spaces = await HappyHourApi.findAllSpaces(apiParams);
        setSpaces(spaces.spaces);
      } catch (error) {
        console.error("Error fetching spaces:", error);
        if (error.response && error.response.status === 404) {
          return { spaces: [] };
        }
      }
    };
    fetchSpaces();
  }, [searchTerm, filters]);

  const getUserSpacesInfo = async () => {
    try {
      const visitedSpacesResponse = await HappyHourApi.getVisited(currentUser.username);
      const likedSpacesResponse = await HappyHourApi.getLikedSpaces(currentUser.username);
      return {
        visitedSpaces: visitedSpacesResponse || [],
        likedSpaces: likedSpacesResponse || [],
      };
    } catch (e) {
      console.error("Error fetching user spaces info", e);
      return { visitedSpaces: [], likedSpaces: [] };
    }
  };

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categoriesData = await HappyHourApi.request("categories");
        setCategories(categoriesData.categories);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };
    getCategories();
  }, []);

  useEffect(() => {
    const getNeighborhoodsandCities = async () => {
      try {
        const locationData = await HappyHourApi.request("locations");
        const uniqueCitiesSet = new Set();
        locationData.locations.forEach((n) => uniqueCitiesSet.add(n.city));
        setNeighborhoods(locationData.locations);
        setCities(Array.from(uniqueCitiesSet));
      } catch (e) {
        console.error("error fetching neighborhoods and cities", e);
      }
    };
    getNeighborhoodsandCities();
  }, []);

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilters({ rating: "", neighborhood: "", city: "", category: "", sortBy: "" });
  };

  return (
    <div className="spaces-page">
      <div className="spaces-page-header">
        <h1 className="spaces-page-title">Spaces</h1>
        {currentUser && currentUser.isAdmin && (
          <Link to="/spaces/new" className="btn-primary-custom">+ Add a Space</Link>
        )}
      </div>

      <div className="spaces-filters">
        <div className="spaces-filter-group search-group">
          <label className="spaces-filter-label">Search</label>
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="spaces-filter-group">
          <label className="spaces-filter-label">Category</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="">All Categories</option>
            {categories && categories.map((category) => (
              <option key={category.cat_id} value={category.cat_type}>
                {category.cat_type}
              </option>
            ))}
          </select>
        </div>

        <div className="spaces-filter-group">
          <label className="spaces-filter-label">City</label>
          <select
            value={filters.city}
            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
          >
            <option value="">All Cities</option>
            {cities && cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        <div className="spaces-filter-group">
          <label className="spaces-filter-label">Neighborhood</label>
          <select
            value={filters.neighborhood}
            onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })}
          >
            <option value="">All Neighborhoods</option>
            {neighborhoods && neighborhoods.map((neighborhood) => (
              <option key={neighborhood.loc_id} value={neighborhood.neighborhood}>
                {neighborhood.city}, {neighborhood.neighborhood}
              </option>
            ))}
          </select>
        </div>

        <div className="spaces-filter-actions">
          <button
            className={`spaces-sort-btn ${filters.sortBy === "DESC" ? "active" : ""}`}
            onClick={() => setFilters({ ...filters, sortBy: "DESC" })}
          >
            ↓ Highest Rated
          </button>
          <button
            className={`spaces-sort-btn ${filters.sortBy === "ASC" ? "active" : ""}`}
            onClick={() => setFilters({ ...filters, sortBy: "ASC" })}
          >
            ↑ Lowest Rated
          </button>
          <button className="spaces-clear-btn" onClick={handleClearSearch}>
            Clear
          </button>
        </div>
      </div>

      {!spaces || spaces.length === 0 ? (
        <p className="spaces-empty">No spaces found.</p>
      ) : (
        <div className="spaces-grid">
          {spaces.map((s) => (
            <SpaceCard
              key={s.space_id}
              title={s.title}
              description={s.description}
              category={s.category}
              address={s.address}
              est_year={s.est_year}
              city={s.city}
              neighborhood={s.neighborhood}
              averageRating={s.avg_rating}
              getUserSpacesInfo={getUserSpacesInfo}
              remove={remove}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SpacesListWithSearch;
